import type { BasicAttackCommand, InputAction } from '../net/protocol.js';

export interface Point { x: number; y: number; }
export interface Camera { x: number; y: number; pixelsPerMetre: number; }
export interface Viewport { cssWidth: number; cssHeight: number; devicePixelRatio: number; }
/** How a skill slot aims: at the nearest enemy, or at the pointer. A setting of the slot, never of the skill. */
export type SkillAimMode = 'AUTO' | 'MOUSE';
export interface InputFrame { moveX: number; moveY: number; aimAngle: number; actions: InputAction[]; basicAttackCommand: BasicAttackCommand; autoSkillSlots: number[]; }

function validCamera(camera: Camera, viewport: Viewport): void {
  if (![camera.x, camera.y, camera.pixelsPerMetre, viewport.cssWidth, viewport.cssHeight, viewport.devicePixelRatio].every(Number.isFinite)
      || camera.pixelsPerMetre <= 0 || viewport.cssWidth < 0 || viewport.cssHeight < 0 || viewport.devicePixelRatio <= 0) throw new Error('invalid camera or viewport');
}
export function worldToScreen(point: Point, camera: Camera, viewport: Viewport): Point {
  validCamera(camera, viewport);
  return { x: (point.x - camera.x) * camera.pixelsPerMetre + viewport.cssWidth / 2, y: (point.y - camera.y) * camera.pixelsPerMetre + viewport.cssHeight / 2 };
}
export function screenToWorld(point: Point, camera: Camera, viewport: Viewport): Point {
  validCamera(camera, viewport);
  return { x: camera.x + (point.x - viewport.cssWidth / 2) / camera.pixelsPerMetre, y: camera.y + (point.y - viewport.cssHeight / 2) / camera.pixelsPerMetre };
}

export class PointerAim {
  private screenPoint: Point | undefined;

  setScreenPoint(point: Point): void {
    if (Number.isFinite(point.x) && Number.isFinite(point.y)) this.screenPoint = { ...point };
  }

  worldPoint(camera: Camera, viewport: Viewport): Point | undefined {
    return this.screenPoint ? screenToWorld(this.screenPoint, camera, viewport) : undefined;
  }
}
const MOVEMENT = new Map<string, Point>([['KeyW', { x: 0, y: -1 }], ['ArrowUp', { x: 0, y: -1 }], ['KeyS', { x: 0, y: 1 }], ['ArrowDown', { x: 0, y: 1 }], ['KeyA', { x: -1, y: 0 }], ['ArrowLeft', { x: -1, y: 0 }], ['KeyD', { x: 1, y: 0 }], ['ArrowRight', { x: 1, y: 0 }]]);
const SLOTS = new Map<string, number>([['Digit1', 0], ['Digit2', 1], ['Digit3', 2], ['Digit4', 3]]);
const DASH_KEYS = new Set(['Space', 'ShiftLeft', 'ShiftRight']);

export class BrowserInput {
  private readonly held = new Set<string>();
  private actions: InputAction[] = [];
  private aim: Point = { x: 0, y: 0 };
  private player: Point = { x: 0, y: 0 };
  private enabled = true;
  private automaticBasicAttack = true;
  private primaryAttackHeld = false;
  private availableSkillSlots: readonly boolean[] = [true, true, true, true];
  private skillAimModes: readonly SkillAimMode[] = ['AUTO', 'AUTO', 'AUTO', 'AUTO'];

  setKey(code: string, down: boolean): void {
    const slot = SLOTS.get(code);
    if (!down) {
      const wasHeld = this.held.delete(code);
      // A pointer-aimed slot casts on release, at the pointer; an automatic one already cast on press.
      if (wasHeld && this.enabled && slot !== undefined && this.availableSkillSlots[slot] && this.skillAimModes[slot] === 'MOUSE') {
        this.actions.push({ type: 'cast', skillSlot: slot, explicitPoint: { ...this.aim } });
      }
      return;
    }
    if (!this.enabled || this.held.has(code)) return;
    if (slot !== undefined && !this.availableSkillSlots[slot]) return;
    // Space and either Shift are aliases of a single held ability. A second
    // alias or browser key-repeat cannot enqueue a duplicate request.
    const dashAlreadyHeld = DASH_KEYS.has(code) && [...DASH_KEYS].some((key) => this.held.has(key));
    this.held.add(code);
    // An automatic slot says nothing about where; the server aims. Holding it still previews.
    if (slot !== undefined && this.skillAimModes[slot] === 'AUTO') this.actions.push({ type: 'cast', skillSlot: slot });
    if (DASH_KEYS.has(code) && !dashAlreadyHeld) this.actions.push({ type: 'dash' });
  }
  setAvailableSkillSlots(available: readonly boolean[]): void {
    this.availableSkillSlots = [0, 1, 2, 3].map((slot) => available[slot] === true);
    for (const [code, slot] of SLOTS) if (!this.availableSkillSlots[slot]) this.held.delete(code);
  }
  setSkillAimModes(modes: readonly SkillAimMode[]): void {
    this.skillAimModes = [0, 1, 2, 3].map((slot) => modes[slot] === 'MOUSE' ? 'MOUSE' : 'AUTO');
  }
  skillAimMode(slot: number): SkillAimMode { return this.skillAimModes[slot] ?? 'AUTO'; }
  heldSkillSlot(): number | undefined {
    for (const [code, slot] of SLOTS) if (this.held.has(code) && this.availableSkillSlots[slot]) return slot;
    return undefined;
  }
  setAim(world: Point): void { if (Number.isFinite(world.x) && Number.isFinite(world.y)) this.aim = { ...world }; }
  setPlayer(world: Point): void { if (Number.isFinite(world.x) && Number.isFinite(world.y)) this.player = { ...world }; }
  setEnabled(enabled: boolean): void { this.enabled = enabled; if (!enabled) this.clear(); }
  setAutomaticBasicAttack(enabled: boolean): void { this.automaticBasicAttack = enabled; }
  setPrimaryAttackHeld(held: boolean): void { this.primaryAttackHeld = held && this.enabled; }
  automaticBasicAttackEnabled(): boolean { return this.automaticBasicAttack; }
  clear(): void { this.held.clear(); this.actions = []; this.primaryAttackHeld = false; }
  consumeFrame(): InputFrame {
    if (!this.enabled) return { moveX: 0, moveY: 0, aimAngle: 0, actions: [], basicAttackCommand: 'WITHHOLD', autoSkillSlots: [] };
    let moveX = 0; let moveY = 0;
    for (const code of this.held) { const direction = MOVEMENT.get(code); if (direction) { moveX += direction.x; moveY += direction.y; } }
    const length = Math.hypot(moveX, moveY); if (length > 1) { moveX /= length; moveY /= length; }
    const actions = this.actions; this.actions = [];
    const aimAngle = Math.atan2(this.aim.y - this.player.y, this.aim.x - this.player.x);
    const basicAttackCommand: BasicAttackCommand = this.automaticBasicAttack ? 'AUTO' : (this.primaryAttackHeld ? 'AIM' : 'WITHHOLD');
    const autoSkillSlots = [0, 1, 2, 3].filter((slot) => this.availableSkillSlots[slot] && this.skillAimModes[slot] === 'AUTO');
    return { moveX, moveY, aimAngle: Number.isFinite(aimAngle) ? aimAngle : 0, actions, basicAttackCommand, autoSkillSlots };
  }
}
